import { useMemo, useState } from "react";
import {
  Alert,
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  IconButton,
  Paper,
  Stack,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import TodayIcon from "@mui/icons-material/Today";
import DownloadIcon from "@mui/icons-material/SaveAlt";
import { nanoid } from "nanoid";
import type {
  LocalizedString,
  MenuDay,
  MenuFeedResult,
  MenuMeal,
  MenuWeek,
  SchemeContent,
  SchemeMenu,
} from "@shared/types/foyer";
import {
  ISO_WEEKDAYS_SV,
  fromIsoDate,
  isoWeekDates,
  isoWeekParts,
  shiftIsoWeek,
  toIsoDate,
} from "@shared/isoWeek";
import {
  DEFAULT_MEAL_SLOTS,
  copyWeekTo,
  countFilledDays,
  countMeals,
  findWeek,
  mealHasContent,
  mergeWeeks,
  upsertWeek,
} from "@shared/menu";
import { resolveLocalized } from "@shared/i18n";
import LocalizedTextField from "../LocalizedTextField";
import { usePremiseScope } from "../../context/PremiseScopeContext";
import { foyerApi } from "../../http/foyer";
import type { ContentPaneProps } from "./ContentEditor";

/**
 * Matsedel - the food-menu editor.
 *
 * Two sources, one screen. "Manual" is the week grid below; "Feed" points at
 * the kitchen's RSS and lets the server keep it current. The stored weeks are
 * never discarded when you switch to Feed, so a premise can adopt a feed,
 * discover it is wrong, and switch straight back to what they had written.
 *
 * The week grid is the load-bearing bit: an operator plans a rotation weeks
 * ahead, so the week NUMBER is the primary axis of navigation (it is what the
 * kitchen's paper schedule is keyed on), the seven days are always visible at
 * once, and "duplicate to next week" exists because a menu is almost always a
 * small edit on the previous week rather than a blank page.
 */

const MONTHS_SV = [
  "jan",
  "feb",
  "mars",
  "april",
  "maj",
  "juni",
  "juli",
  "aug",
  "sep",
  "okt",
  "nov",
  "dec",
];

/** "27 juli - 2 aug" for a week's date span. */
function spanLabel(dates: string[]): string {
  const a = fromIsoDate(dates[0]);
  const b = fromIsoDate(dates[6]);
  if (!a || !b) return "";
  return `${a.getDate()} ${MONTHS_SV[a.getMonth()]} - ${b.getDate()} ${MONTHS_SV[b.getMonth()]}`;
}

/** Map a `menu_feed_*` code to something an operator can act on. */
function feedErrorMessage(code: string | undefined): string {
  switch (code) {
    case "menu_feed_bad_url":
      return "That is not a valid web address. It should start with http:// or https://.";
    case "menu_feed_blocked_host":
      return "That address points at a private or internal host, which we will not fetch.";
    case "menu_feed_timeout":
      return "The kitchen's server did not answer in time. Check the address, or try again shortly.";
    case "menu_feed_unparseable":
      return "That address answered, but not with RSS or Atom. Check you copied the feed link and not the web page.";
    case "menu_feed_empty":
      return "The feed loaded but contains no entries.";
    case "menu_feed_too_large":
      return "That feed is far larger than a menu should be - check the address.";
    default:
      return "Could not read that feed. Check the address and that the kitchen's server is reachable.";
  }
}

export default function MenuEditor({
  content,
  supportedLocales,
  defaultLocale,
  onChange,
}: ContentPaneProps) {
  const premiseId = usePremiseScope();
  const menu: SchemeMenu = content.menu ?? {};
  const source = menu.source ?? "manual";
  const slots = menu.mealSlots?.length ? menu.mealSlots : DEFAULT_MEAL_SLOTS;

  const setMenu = (part: Partial<SchemeMenu>) =>
    onChange({ ...content, menu: { ...menu, ...part } } as SchemeContent);

  // ── Week navigation ─────────────────────────────────────────────────────
  const today = useMemo(() => new Date(), []);
  const nowParts = useMemo(() => isoWeekParts(today), [today]);
  const [cursor, setCursor] = useState<{ year: number; week: number }>(
    nowParts
  );

  const dates = useMemo(
    () => isoWeekDates(cursor.year, cursor.week),
    [cursor.year, cursor.week]
  );
  const week = findWeek(menu.weeks, cursor.year, cursor.week);
  const todayIso = toIsoDate(today);
  const isCurrentWeek =
    cursor.year === nowParts.year && cursor.week === nowParts.week;

  /** Write the edited week back, pruning it away entirely if it ends up empty. */
  const writeWeek = (next: MenuWeek) =>
    setMenu({ weeks: upsertWeek(menu.weeks, next) });

  /** The week currently under the cursor, materialised so edits have something
   *  to write into even before anything has been typed. */
  const draftWeek: MenuWeek = week ?? {
    year: cursor.year,
    week: cursor.week,
    days: [],
  };

  const dayFor = (date: string): MenuDay =>
    draftWeek.days.find((d) => d.date === date) ?? { date, meals: [] };

  const setDay = (date: string, meals: MenuMeal[]) => {
    const others = draftWeek.days.filter((d) => d.date !== date);
    writeWeek({
      ...draftWeek,
      days: [...others, { date, meals }].sort((a, b) =>
        a.date.localeCompare(b.date)
      ),
    });
  };

  const addMeal = (date: string, label: LocalizedString = "") =>
    setDay(date, [...dayFor(date).meals, { id: nanoid(8), label, body: "" }]);

  /** Give a day the premise's full set of serving slots in one click. */
  const fillDayWithSlots = (date: string) => {
    const existing = dayFor(date).meals;
    const have = new Set(
      existing.map((m) =>
        resolveLocalized(m.label, defaultLocale, defaultLocale)
      )
    );
    const additions = slots
      .filter(
        (s) =>
          !have.has(resolveLocalized(s.label, defaultLocale, defaultLocale))
      )
      .map((s) => ({ id: nanoid(8), label: s.label, body: "" }));
    setDay(date, [...existing, ...additions]);
  };

  const weeksWithContent = useMemo(
    () =>
      (menu.weeks ?? [])
        .filter((w) => countMeals(w) > 0)
        .map((w) => ({
          year: w.year,
          week: w.week,
          meals: countMeals(w),
        })),
    [menu.weeks]
  );

  return (
    <Stack spacing={3} data-testid="menu-editor">
      <Box>
        <Typography variant="h6">Matsedel</Typography>
        <Typography variant="body2" color="text.secondary">
          The week&apos;s meals, Monday to Sunday, shown on the TV under
          &quot;Matsedel&quot;. Author them here, or connect the kitchen&apos;s
          RSS feed and let it keep itself current.
        </Typography>
      </Box>

      <Tabs
        value={source}
        onChange={(_, v) => setMenu({ source: v as SchemeMenu["source"] })}
        sx={{ borderBottom: 1, borderColor: "divider" }}
      >
        <Tab
          value="manual"
          label="Write it myself"
          data-testid="menu-source-manual"
        />
        <Tab
          value="feed"
          label="Import an RSS feed"
          data-testid="menu-source-feed"
        />
      </Tabs>

      {source === "feed" && (
        <FeedPanel
          menu={menu}
          premiseId={premiseId}
          onChange={setMenu}
          onAdopt={(weeks) =>
            setMenu({ weeks: mergeWeeks(menu.weeks, weeks), source: "manual" })
          }
        />
      )}

      {source === "feed" && (
        <Alert severity="info">
          The TV shows the feed. The weeks below are kept as a fallback — if the
          kitchen&apos;s server is unreachable, the TV falls back to them rather
          than showing an empty screen.
        </Alert>
      )}

      {/* ── Week navigator ─────────────────────────────────────────────── */}
      <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          flexWrap="wrap"
          gap={1.5}
        >
          <Stack direction="row" alignItems="center" spacing={1}>
            <IconButton
              size="small"
              aria-label="Previous week"
              onClick={() =>
                setCursor(shiftIsoWeek(cursor.year, cursor.week, -1))
              }
            >
              <ChevronLeftIcon />
            </IconButton>
            <Box sx={{ textAlign: "center", minWidth: 190 }}>
              <Typography variant="h5" sx={{ lineHeight: 1.2 }}>
                Vecka {cursor.week}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {spanLabel(dates)} · {cursor.year}
              </Typography>
            </Box>
            <IconButton
              size="small"
              aria-label="Next week"
              onClick={() =>
                setCursor(shiftIsoWeek(cursor.year, cursor.week, 1))
              }
            >
              <ChevronRightIcon />
            </IconButton>
            {isCurrentWeek ? (
              <Chip size="small" color="primary" label="This week" />
            ) : (
              <Button
                size="small"
                startIcon={<TodayIcon />}
                onClick={() => setCursor(nowParts)}
              >
                This week
              </Button>
            )}
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1}>
            <Chip
              size="small"
              variant="outlined"
              label={`${countFilledDays(week)}/7 days · ${countMeals(week)} meals`}
            />
            <Tooltip title="Copy this week's meals onto next week, then edit the differences">
              <span>
                <Button
                  size="small"
                  startIcon={<ContentCopyIcon />}
                  disabled={countMeals(week) === 0}
                  onClick={() => {
                    const next = shiftIsoWeek(cursor.year, cursor.week, 1);
                    setMenu({
                      weeks: upsertWeek(
                        menu.weeks,
                        copyWeekTo(draftWeek, next.year, next.week)
                      ),
                    });
                    setCursor(next);
                  }}
                >
                  Duplicate to next week
                </Button>
              </span>
            </Tooltip>
            <Tooltip title="Remove every meal in this week">
              <span>
                <Button
                  size="small"
                  color="error"
                  startIcon={<DeleteIcon />}
                  disabled={countMeals(week) === 0}
                  onClick={() => writeWeek({ ...draftWeek, days: [] })}
                >
                  Clear week
                </Button>
              </span>
            </Tooltip>
          </Stack>
        </Stack>

        {/* Jump-to chips for every authored week, so "which weeks have I done?"
            is answerable at a glance instead of by paging through the year. */}
        {weeksWithContent.length > 0 && (
          <>
            <Divider sx={{ my: 1.5 }} />
            <Stack
              direction="row"
              spacing={0.75}
              flexWrap="wrap"
              useFlexGap
              alignItems="center"
            >
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ mr: 1 }}
              >
                Authored weeks
              </Typography>
              {weeksWithContent.map((w) => (
                <Chip
                  key={`${w.year}-${w.week}`}
                  size="small"
                  label={`v.${w.week}`}
                  color={
                    w.year === cursor.year && w.week === cursor.week
                      ? "primary"
                      : "default"
                  }
                  variant={
                    w.year === cursor.year && w.week === cursor.week
                      ? "filled"
                      : "outlined"
                  }
                  onClick={() => setCursor({ year: w.year, week: w.week })}
                />
              ))}
            </Stack>
          </>
        )}
      </Paper>

      {/* ── The seven days ─────────────────────────────────────────────── */}
      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fill, minmax(330px, 1fr))",
          gap: 2,
          alignItems: "start",
        }}
      >
        {dates.map((date, i) => {
          const day = dayFor(date);
          const parsed = fromIsoDate(date);
          const isToday = date === todayIso;
          return (
            <Paper
              key={date}
              variant="outlined"
              data-testid={`menu-day-${date}`}
              sx={{
                p: 2,
                borderColor: isToday ? "primary.main" : undefined,
                borderWidth: isToday ? 2 : 1,
              }}
            >
              <Stack
                direction="row"
                alignItems="baseline"
                justifyContent="space-between"
                sx={{ mb: 1 }}
              >
                <Box>
                  <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
                    {ISO_WEEKDAYS_SV[i]}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {parsed
                      ? `${parsed.getDate()} ${MONTHS_SV[parsed.getMonth()]}`
                      : date}
                    {isToday && " · idag"}
                  </Typography>
                </Box>
                <Badge
                  badgeContent={day.meals.filter(mealHasContent).length}
                  color="primary"
                  showZero={false}
                />
              </Stack>

              <Stack spacing={2}>
                {day.meals.map((meal, mi) => (
                  <Box key={meal.id}>
                    <Stack
                      direction="row"
                      alignItems="center"
                      spacing={1}
                      sx={{ mb: 0.5 }}
                    >
                      <Box sx={{ flex: 1 }}>
                        <LocalizedTextField
                          label="Serving"
                          value={meal.label}
                          supportedLocales={supportedLocales}
                          defaultLocale={defaultLocale}
                          placeholder="Lunch · 12:00-13:00"
                          onChange={(label) =>
                            setDay(
                              date,
                              day.meals.map((m, x) =>
                                x === mi ? { ...m, label } : m
                              )
                            )
                          }
                        />
                      </Box>
                      <IconButton
                        size="small"
                        aria-label={`Remove serving ${mi + 1}`}
                        onClick={() =>
                          setDay(
                            date,
                            day.meals.filter((_, x) => x !== mi)
                          )
                        }
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Stack>
                    <LocalizedTextField
                      label="Dish"
                      value={meal.body}
                      supportedLocales={supportedLocales}
                      defaultLocale={defaultLocale}
                      multiline
                      rows={2}
                      placeholder="Köttbullar med gräddsås, pressgurka och lingon"
                      onChange={(body) =>
                        setDay(
                          date,
                          day.meals.map((m, x) =>
                            x === mi ? { ...m, body } : m
                          )
                        )
                      }
                    />
                    <Box sx={{ mt: 1 }}>
                      <LocalizedTextField
                        label="Note (optional)"
                        value={meal.note}
                        supportedLocales={supportedLocales}
                        defaultLocale={defaultLocale}
                        placeholder="Vegetariskt alternativ: rotfruktsgratäng"
                        onChange={(note) =>
                          setDay(
                            date,
                            day.meals.map((m, x) =>
                              x === mi ? { ...m, note } : m
                            )
                          )
                        }
                      />
                    </Box>
                    {mi < day.meals.length - 1 && <Divider sx={{ mt: 2 }} />}
                  </Box>
                ))}
              </Stack>

              <Stack direction="row" spacing={1} sx={{ mt: 1.5 }}>
                <Button
                  size="small"
                  startIcon={<AddIcon />}
                  onClick={() => addMeal(date)}
                >
                  Serving
                </Button>
                {day.meals.length === 0 && (
                  <Button size="small" onClick={() => fillDayWithSlots(date)}>
                    Use standard servings
                  </Button>
                )}
              </Stack>
            </Paper>
          );
        })}
      </Box>

      <MealSlotsPanel
        menu={menu}
        supportedLocales={supportedLocales}
        defaultLocale={defaultLocale}
        onChange={setMenu}
      />
    </Stack>
  );
}

// ── RSS panel ──────────────────────────────────────────────────────────────

function FeedPanel({
  menu,
  premiseId,
  onChange,
  onAdopt,
}: {
  menu: SchemeMenu;
  premiseId: string | undefined;
  onChange: (part: Partial<SchemeMenu>) => void;
  onAdopt: (weeks: MenuWeek[]) => void;
}) {
  const [url, setUrl] = useState(menu.feed?.url ?? "");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [result, setResult] = useState<MenuFeedResult | null>(null);

  const test = async () => {
    if (!premiseId) return;
    setBusy(true);
    setError(null);
    setResult(null);
    try {
      const out = await foyerApi.previewMenuFeed(premiseId, url.trim());
      setResult(out);
      // A successful read is also the operator's intent to use this URL, so
      // store it - they still have to Save the premise for it to take effect.
      onChange({ feed: { ...menu.feed, url: url.trim() } });
    } catch (err) {
      setError(feedErrorMessage((err as { code?: string } | undefined)?.code));
    } finally {
      setBusy(false);
    }
  };

  const totalDays = (result?.weeks ?? []).reduce(
    (n, w) => n + w.days.length,
    0
  );

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Stack spacing={2}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Kitchen feed
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Paste the RSS or Atom address the kitchen publishes. We read it on a
            schedule and place each entry on its day — by its date when it has
            one, otherwise by the weekday and week number in its title.
          </Typography>
        </Box>

        <Stack direction="row" spacing={1} alignItems="flex-start">
          <TextField
            size="small"
            fullWidth
            label="Feed address"
            placeholder="https://kommun.se/matsedel/solgarden.rss"
            value={url}
            onChange={(e) => setUrl(e.target.value)}
            inputProps={{ "data-testid": "menu-feed-url" }}
          />
          <TextField
            size="small"
            select
            label="Refresh"
            value={menu.feed?.refreshMinutes ?? 60}
            onChange={(e) =>
              onChange({
                feed: {
                  url: menu.feed?.url ?? url.trim(),
                  refreshMinutes: Number(e.target.value),
                },
              })
            }
            sx={{ minWidth: 140 }}
            SelectProps={{ native: true }}
          >
            <option value={15}>Every 15 min</option>
            <option value={60}>Hourly</option>
            <option value={360}>Every 6 hours</option>
            <option value={1440}>Daily</option>
          </TextField>
          <Button
            variant="contained"
            disabled={!url.trim() || busy || !premiseId}
            onClick={test}
            startIcon={busy ? <CircularProgress size={16} /> : undefined}
            sx={{ whiteSpace: "nowrap" }}
            data-testid="menu-feed-test"
          >
            Test feed
          </Button>
        </Stack>

        {!premiseId && (
          <Alert severity="info">
            Save the premise once before testing a feed — we fetch it on behalf
            of this premise.
          </Alert>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        {result && (
          <Stack spacing={1.5}>
            <Alert
              severity={totalDays > 0 ? "success" : "warning"}
              action={
                result.weeks.length > 0 ? (
                  <Button
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={() => onAdopt(result.weeks)}
                  >
                    Copy into my weeks
                  </Button>
                ) : undefined
              }
            >
              {totalDays > 0
                ? `Read ${totalDays} day${totalDays === 1 ? "" : "s"} across ${result.weeks.length} week${result.weeks.length === 1 ? "" : "s"}${result.feedTitle ? ` from "${result.feedTitle}"` : ""}.`
                : "The feed loaded, but we could not place any entry on a day."}
            </Alert>

            {result.weeks.map((w) => (
              <Box key={`${w.year}-${w.week}`}>
                <Typography variant="subtitle2">
                  Vecka {w.week} · {spanLabel(isoWeekDates(w.year, w.week))}
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.75}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mt: 0.5 }}
                >
                  {w.days.map((d) => {
                    const parsed = fromIsoDate(d.date);
                    const idx = parsed ? (parsed.getDay() + 6) % 7 : 0;
                    return (
                      <Tooltip
                        key={d.date}
                        title={d.meals
                          .map(
                            (m) =>
                              `${typeof m.label === "string" ? m.label : ""}: ${typeof m.body === "string" ? m.body : ""}`
                          )
                          .join("\n")}
                      >
                        <Chip
                          size="small"
                          variant="outlined"
                          label={`${ISO_WEEKDAYS_SV[idx].slice(0, 3)} · ${d.meals.length}`}
                        />
                      </Tooltip>
                    );
                  })}
                </Stack>
              </Box>
            ))}

            {/* Never silently drop what we did not understand - the operator is
                the only one who can tell whether it mattered. */}
            {result.unplaced.length > 0 && (
              <Alert severity="warning">
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  {result.unplaced.length} entr
                  {result.unplaced.length === 1 ? "y" : "ies"} could not be
                  placed on a day
                </Typography>
                <Typography variant="caption" component="div" sx={{ mt: 0.5 }}>
                  They carry no date, weekday or week number we recognise. They
                  will not appear on the TV — add those days by hand if they
                  matter.
                </Typography>
                <Stack
                  direction="row"
                  spacing={0.75}
                  flexWrap="wrap"
                  useFlexGap
                  sx={{ mt: 1 }}
                >
                  {result.unplaced.slice(0, 8).map((u, i) => (
                    <Chip
                      key={i}
                      size="small"
                      variant="outlined"
                      label={u.title || u.body.slice(0, 40) || "(untitled)"}
                    />
                  ))}
                </Stack>
              </Alert>
            )}
          </Stack>
        )}
      </Stack>
    </Paper>
  );
}

// ── Serving slots ──────────────────────────────────────────────────────────

/**
 * The premise's standard servings. Purely a labour-saver: "Use standard
 * servings" stamps these onto an empty day so an operator types dishes rather
 * than retyping "Lunch · 12:00-13:00" seven times a week.
 */
function MealSlotsPanel({
  menu,
  supportedLocales,
  defaultLocale,
  onChange,
}: {
  menu: SchemeMenu;
  supportedLocales: string[];
  defaultLocale: string;
  onChange: (part: Partial<SchemeMenu>) => void;
}) {
  const slots = menu.mealSlots?.length ? menu.mealSlots : DEFAULT_MEAL_SLOTS;
  const write = (next: typeof slots) => onChange({ mealSlots: next });

  return (
    <Paper variant="outlined" sx={{ p: 2 }}>
      <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
        Standard servings
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        The servings this premise offers on an ordinary day. Used by &quot;Use
        standard servings&quot; to pre-fill an empty day.
      </Typography>
      <Stack spacing={1}>
        {slots.map((slot, i) => (
          <Stack key={slot.id} direction="row" spacing={1} alignItems="center">
            <Box sx={{ flex: 1 }}>
              <LocalizedTextField
                value={slot.label}
                supportedLocales={supportedLocales}
                defaultLocale={defaultLocale}
                onChange={(label) =>
                  write(slots.map((s, x) => (x === i ? { ...s, label } : s)))
                }
              />
            </Box>
            <IconButton
              size="small"
              aria-label={`Remove standard serving ${i + 1}`}
              disabled={slots.length <= 1}
              onClick={() => write(slots.filter((_, x) => x !== i))}
            >
              <DeleteIcon fontSize="small" />
            </IconButton>
          </Stack>
        ))}
      </Stack>
      <Button
        size="small"
        startIcon={<AddIcon />}
        sx={{ mt: 1 }}
        onClick={() => write([...slots, { id: nanoid(8), label: "" }])}
      >
        Standard serving
      </Button>
    </Paper>
  );
}

// Re-exported so tests can assert on the copy without duplicating it.
export { feedErrorMessage, spanLabel };
